import { useCallback, useState, type Dispatch, type SetStateAction } from "react";
import { readCookie, writeCookie } from "../utils/cookie";

// useDevicePref — a DEVICE-scoped preference (VW preferences layer, M3). Persisted in a cookie so it
// survives refresh on THIS device but does NOT sync across the user's devices — the correct home for
// ergonomic UI chrome (panel collapse, widths, "which panel is open on this monitor"), the opposite of
// account-scoped IDENTITY prefs (theme, saved views) which live in the DB and follow the person.
//
// This is the typed primitive behind the app's usePreference(key, { scope: "device" }); it replaces the
// bespoke shell.* cookie reads/writes that were scattered across the shell (the M3 drift). Cookies only —
// never localStorage (VW state policy). An optional validator rejects stale/unknown stored values.
export function useDevicePref<T extends string>(
  cookieKey: string,
  fallback: T,
  isValid?: (v: string) => v is T,
): readonly [T, Dispatch<SetStateAction<T>>] {
  const [value, setValue] = useState<T>(() => {
    const stored = readCookie(cookieKey);
    if (stored != null && (isValid ? isValid(stored) : true)) return stored as T;
    return fallback;
  });
  // Drop-in for a useState setter (supports functional updates), and persists the resolved value to the
  // cookie on every set — so callers just swap `useState` → `useDevicePref` and get persistence for free.
  const set = useCallback<Dispatch<SetStateAction<T>>>(
    (action) => {
      setValue((prev) => {
        const next = typeof action === "function" ? (action as (p: T) => T)(prev) : action;
        writeCookie(cookieKey, next, { sameSite: "Lax", path: "/" });
        return next;
      });
    },
    [cookieKey],
  );
  return [value, set] as const;
}
